import type { ReactNode } from "react";

import { CookieSettingsButton } from "#/components/CookieConsent";
import { SectionHeader } from "#/components/SectionHeader";

function PolicySupplementSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-b border-border pb-10 last:border-b-0 last:pb-0">
      <SectionHeader>{title}</SectionHeader>
      {children}
    </section>
  );
}

function PolicyParagraph({ children }: { children: ReactNode }) {
  return <p className="text-[15px] leading-7 text-muted-foreground">{children}</p>;
}

export function CookieManagePreferencesSection() {
  return (
    <PolicySupplementSection title="Manage your preferences">
      <PolicyParagraph>
        You can change or withdraw consent at any time using our cookie settings panel, also linked
        in the site footer.
      </PolicyParagraph>
      <p className="text-[15px] leading-7">
        <CookieSettingsButton className="text-primary" />
      </p>
    </PolicySupplementSection>
  );
}

export function CookiePaymentPagesSection() {
  return (
    <PolicySupplementSection title="Premium checkout">
      <PolicyParagraph>
        Polar acts as Merchant of Record for Premium purchases. Polar is the seller, processes
        payment and tax, and may set cookies on its checkout or customer portal pages when you are
        redirected there. We do not receive full card numbers.
      </PolicyParagraph>
    </PolicySupplementSection>
  );
}

export function PrivacySecuritySection() {
  return (
    <PolicySupplementSection title="Security">
      <PolicyParagraph>
        We protect personal information with measures appropriate to the risk, including encryption
        in transit (HTTPS), access controls on production systems, and limiting staff access to
        operational data. No method of transmission or storage is completely secure; if you believe
        your account has been compromised, contact us at privacy@playdailywars.com.
      </PolicyParagraph>
    </PolicySupplementSection>
  );
}

export function PrivacyContactSection() {
  return (
    <PolicySupplementSection title="How to contact us">
      <PolicyParagraph>
        For privacy requests, data subject rights, and questions about this policy, email{" "}
        <a
          href="mailto:privacy@playdailywars.com"
          className="text-primary underline underline-offset-4 hover:text-foreground"
        >
          privacy@playdailywars.com
        </a>
        . For account help, billing, or refunds, email{" "}
        <a
          href="mailto:support@playdailywars.com"
          className="text-primary underline underline-offset-4 hover:text-foreground"
        >
          support@playdailywars.com
        </a>
        .
      </PolicyParagraph>
    </PolicySupplementSection>
  );
}

export function PrivacyDoNotSellSection() {
  return (
    <PolicySupplementSection title="Sale and sharing of personal information">
      <PolicyParagraph>
        We do not sell your personal information. We do not share it for cross-context behavioral
        advertising. Where privacy laws provide a right to opt out of sale or sharing, that right is
        included for completeness, but it does not apply to our current practices.
      </PolicyParagraph>
    </PolicySupplementSection>
  );
}

export function PolicyUpdatesSection() {
  return (
    <PolicySupplementSection title="Changes to this policy">
      <PolicyParagraph>
        We may update this policy from time to time. When we make material changes, we will update
        the effective date at the top of the page and, where required, prompt you to review cookie
        preferences again. Continued use of the service after the effective date means you accept
        the updated policy, subject to your rights under applicable law.
      </PolicyParagraph>
    </PolicySupplementSection>
  );
}
